import { useEffect, useState } from "react";
import { supabase } from "@/integrations/supabase/client";
import { getStripeEnvironment } from "@/lib/stripe";
import { useAuth } from "@/hooks/use-auth";

export type PlanName = "free" | "pro" | "premium";

export interface PlanLimits {
  plan: PlanName;
  display_name: string;
  saved_games_limit: number | null;
  active_pools_limit: number | null;
  pool_participants_limit: number | null;
  email_alerts: boolean;
  closure_generator: boolean;
  advanced_stats: boolean;
  exports: boolean;
  priority_support: boolean;
  monthly_price_brl: number | null;
  yearly_price_brl: number | null;
}

export interface SubscriptionRow {
  id: string;
  status: string;
  price_id: string;
  product_id: string;
  current_period_end: string | null;
  cancel_at_period_end: boolean;
  trial_end: string | null;
  environment: string;
  stripe_customer_id: string | null;
  stripe_subscription_id: string | null;
}

function priceIdToPlan(priceId: string | undefined | null): PlanName {
  if (!priceId) return "free";
  if (priceId.startsWith("lt_premium")) return "premium";
  if (priceId.startsWith("lt_pro")) return "pro";
  return "free";
}

const readPlanPrices = async () => {
  const { data } = await supabase.from("app_settings").select("value").eq("key", "plan_prices").maybeSingle();
  return (data?.value ?? {}) as Partial<Record<PlanName, unknown>>;
};

const toNumber = (value: unknown): number | null => {
  if (value === null || value === undefined || value === "") return null;
  const n = typeof value === "number" ? value : Number(String(value).replace(",", "."));
  return Number.isFinite(n) ? n : null;
};

export function isActiveStatus(s: SubscriptionRow | null): boolean {
  if (!s) return false;
  const futureEnd = !s.current_period_end || new Date(s.current_period_end) > new Date();
  if (["active", "trialing", "past_due"].includes(s.status) && futureEnd) return true;
  if (s.status === "canceled" && s.current_period_end && new Date(s.current_period_end) > new Date()) return true;
  return false;
}

export function useSubscription() {
  const { user } = useAuth();
  const [subscription, setSubscription] = useState<SubscriptionRow | null>(null);
  const [limits, setLimits] = useState<PlanLimits | null>(null);
  const [loading, setLoading] = useState(true);

  const env = getStripeEnvironment();

  const refresh = async () => {
    if (!user) {
      setSubscription(null);
      setLoading(false);
      return;
    }
    const { data: sub } = await supabase
      .from("subscriptions")
      .select("id, status, price_id, product_id, current_period_end, cancel_at_period_end, trial_end, environment, stripe_customer_id, stripe_subscription_id")
      .eq("user_id", user.id)
      .eq("environment", env)
      .order("created_at", { ascending: false })
      .limit(1)
      .maybeSingle();

    setSubscription((sub as SubscriptionRow | null) ?? null);

    const planName = isActiveStatus(sub as SubscriptionRow | null)
      ? priceIdToPlan((sub as SubscriptionRow | null)?.price_id)
      : "free";

    const [{ data: planLimits }, configuredPrices] = await Promise.all([
      supabase.from("plan_limits").select("*").eq("plan", planName).maybeSingle(),
      readPlanPrices(),
    ]);

    const nextLimits = (planLimits as PlanLimits | null) ?? null;
    const configuredMonthly = toNumber(configuredPrices[planName]);
    setLimits(nextLimits ? { ...nextLimits, monthly_price_brl: configuredMonthly ?? nextLimits.monthly_price_brl } : null);
    setLoading(false);
  };

  useEffect(() => {
    setLoading(true);
    refresh();

    if (!user) return;

    const channel = supabase
      .channel(`use-subscription:${user.id}:${Math.random().toString(36).slice(2)}`)
      .on(
        "postgres_changes",
        {
          event: "*",
          schema: "public",
          table: "subscriptions",
          filter: `user_id=eq.${user.id}`,
        },
        () => refresh(),
      )
      .subscribe();

    return () => {
      supabase.removeChannel(channel);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [user?.id]);

  const plan: PlanName = isActiveStatus(subscription)
    ? priceIdToPlan(subscription?.price_id)
    : "free";

  return {
    loading,
    plan,
    isPaid: plan !== "free",
    isActive: isActiveStatus(subscription),
    subscription,
    limits,
    refresh,
  };
}